import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ChevronDown, ChevronRight, Download, Play, RefreshCw, Save, Square } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
const INDEXES = ["NIFTY", "BANKNIFTY", "SENSEX", "BANKEX"];
const HOURLY_PERIODS = [
  "09:15 - 10:15",
  "10:15 - 11:15",
  "11:15 - 12:15",
  "12:15 - 13:15",
  "13:15 - 14:15",
  "14:15 - 15:15",
  "15:15 - 15:30",
];

const colors = {
  background: "#020202",
  panel: "#070604",
  card: "#0b0905",
  gold: "#f7c948",
  dimGold: "#9f7a24",
  green: "#22c55e",
  red: "#ef4444",
  blue: "#38bdf8",
  line: "rgba(247,201,72,0.26)",
};

function number(value) {
  return Number(value || 0);
}

function fmt(value, digits = 0) {
  return number(value).toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function signed(value, digits = 0) {
  const next = number(value);
  return `${next >= 0 ? "+" : ""}${fmt(next, digits)}`;
}

function localDate(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function addDays(date, days) {
  const next = new Date(`${date}T12:00:00+05:30`);
  next.setDate(next.getDate() + days);
  return localDate(next);
}

function timeToMinutes(value) {
  const [hour, minute] = String(value || "00:00").split(":").map(Number);
  return hour * 60 + minute;
}

function minuteLabel(totalMinutes) {
  return `${String(Math.floor(totalMinutes / 60)).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`;
}

function recordMinute(record) {
  const capturedAt = new Date(record?.capturedAt).getTime();
  if (Number.isFinite(capturedAt)) {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(capturedAt));
    const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
    return number(values.hour) * 60 + number(values.minute) + number(values.second) / 60;
  }

  const [hour, minute, second] = String(record?.time || "00:00:00").split(":").map(Number);
  return number(hour) * 60 + number(minute) + number(second) / 60;
}

function snapshotAtBoundary(records, targetMinute, maxDelayMinutes = 1) {
  return records.find(record => {
    const minute = recordMinute(record);
    return minute >= targetMinute && minute < targetMinute + maxDelayMinutes;
  });
}

function buildIntervalLedger(records, date, index, period, intervalMinutes) {
  const [startText, endText] = period.split(" - ");
  const start = timeToMinutes(startText);
  const end = timeToMinutes(endText);
  const dayRecords = records
    .filter(row => row.date === date && row.index === index)
    .sort((a, b) => new Date(a.capturedAt) - new Date(b.capturedAt));

  return Array.from(
    { length: Math.ceil((end - start) / intervalMinutes) },
    (_, position) => {
      const intervalStart = start + position * intervalMinutes;
      const intervalEnd = Math.min(intervalStart + intervalMinutes, end);
      const open = snapshotAtBoundary(dayRecords, intervalStart);
      const close = snapshotAtBoundary(dayRecords, intervalEnd);
      const base = {
        serial: position + 1,
        label: `${minuteLabel(intervalStart)} - ${minuteLabel(intervalEnd)}`,
        open,
        close,
      };
      if (!open || !close) return base;

      const deltas = {
        ceSellers: number(close.ceSellers) - number(open.ceSellers),
        peSellers: number(close.peSellers) - number(open.peSellers),
        ceVolume: number(close.ceVolume) - number(open.ceVolume),
        peVolume: number(close.peVolume) - number(open.peVolume),
      };
      return {
        ...base,
        deltas,
        indexImpact: Number((number(close.spot) - number(open.spot)).toFixed(2)),
        strikeImpact: strongestStrikeImpact(open, close),
      };
    }
  );
}

function strongestStrikeImpact(open, close) {
  const candidates = [
    ...sideStrikeImpacts(open?.callStrikes, close?.callStrikes, "CE"),
    ...sideStrikeImpacts(open?.putStrikes, close?.putStrikes, "PE"),
  ].sort((a, b) => Math.abs(b.points) - Math.abs(a.points));
  return candidates[0] || null;
}

function sideStrikeImpacts(openRows = [], closeRows = [], side) {
  const openByStrike = new Map(openRows.map(row => [String(row.strike), row]));
  return closeRows
    .filter(row => openByStrike.has(String(row.strike)))
    .map(row => ({
      side,
      strike: row.strike,
      points: Number((number(row.ltp) - number(openByStrike.get(String(row.strike))?.ltp)).toFixed(2)),
    }));
}

function intervalStrikeRows(row) {
  if (!row?.open) return [];

  const buildRows = (openRows = [], closeRows = [], side) => {
    const closeByStrike = new Map(closeRows.map(option => [String(option.strike), option]));
    return openRows.map(option => {
      const closeOption = closeByStrike.get(String(option.strike));
      const moneyness = option.moneyness || (
        option.strike === row.open.atmStrike
          ? "ATM"
          : side === "CE"
            ? option.strike < row.open.atmStrike ? "ITM" : "OTM"
            : option.strike > row.open.atmStrike ? "ITM" : "OTM"
      );
      return {
        side,
        moneyness,
        strike: option.strike,
        ltp: option.ltp,
        ltpChange: closeOption
          ? Number((number(closeOption.ltp) - number(option.ltp)).toFixed(2))
          : null,
        sellers: option.oi,
        sellerChange: closeOption ? number(closeOption.oi) - number(option.oi) : null,
        volume: option.volume,
        volumeChange: closeOption ? number(closeOption.volume) - number(option.volume) : null,
        delta: number((closeOption || option).delta),
        gamma: number((closeOption || option).gamma),
        theta: number((closeOption || option).theta),
        vega: number((closeOption || option).vega),
        iv: number((closeOption || option).iv),
        openDelta: number(option.delta),
        openGamma: number(option.gamma),
        openTheta: number(option.theta),
        openVega: number(option.vega),
        openIv: number(option.iv),
        deltaChange: closeOption ? number(closeOption.delta) - number(option.delta) : null,
        gammaChange: closeOption ? number(closeOption.gamma) - number(option.gamma) : null,
        thetaChange: closeOption ? number(closeOption.theta) - number(option.theta) : null,
        vegaChange: closeOption ? number(closeOption.vega) - number(option.vega) : null,
        ivChange: closeOption ? number(closeOption.iv) - number(option.iv) : null,
      };
    });
  };

  return [
    ...buildRows(row.open.callStrikes, row.close?.callStrikes, "CE"),
    ...buildRows(row.open.putStrikes, row.close?.putStrikes, "PE"),
  ];
}

function rapidIndicatorRows(row, intervalMinutes = 1) {
  if (!row?.open || !row?.close) return [];
  const spotMove = number(row.close.spot) - number(row.open.spot);
  const elapsedDays = intervalMinutes / (24 * 60);
  const sideVolumes = {
    CE: Math.max(0, number(row.deltas?.ceVolume)),
    PE: Math.max(0, number(row.deltas?.peVolume)),
  };

  return intervalStrikeRows(row)
    .filter(option => option.moneyness === "OTM" && option.ltpChange !== null)
    .map(option => {
      const openLtp = Math.max(number(option.ltp), 0.05);
      const ltpChange = number(option.ltpChange);
      const ltpChangePercent = (ltpChange / openLtp) * 100;
      const deltaChange = number(option.deltaChange);
      const gammaRatio = option.openGamma > 0 ? number(option.gamma) / option.openGamma : 0;
      const ivChange = number(option.ivChange);
      const volumeChange = number(option.volumeChange);
      const requiredVolume = Math.max(1000, sideVolumes[option.side] * 0.05);
      const directionConfirmed = option.side === "CE"
        ? spotMove > 0 && ltpChange > 0 && deltaChange > 0
        : spotMove < 0 && ltpChange > 0 && Math.abs(option.delta) > Math.abs(option.openDelta);

      const checks = {
        premium: Math.abs(ltpChangePercent) >= 20,
        delta: Math.abs(deltaChange) >= 0.08,
        gamma: option.openGamma > 0 && gammaRatio >= 1.2,
        iv: ivChange >= 1.5,
        volume: volumeChange >= requiredVolume,
      };
      const agreementCount = Object.values(checks).filter(Boolean).length;
      const thetaDrag = Math.abs(number(option.openTheta) * elapsedDays);
      const deltaEffect = number(option.openDelta) * spotMove;
      const gammaEffect = 0.5 * number(option.openGamma) * spotMove * spotMove;
      const vegaEffect = number(option.openVega) * ivChange;
      const thetaEffect = number(option.openTheta) * elapsedDays;
      const modeledMove = deltaEffect + gammaEffect + vegaEffect + thetaEffect;
      const thetaDominant = thetaDrag > Math.abs(deltaEffect + gammaEffect + vegaEffect);

      const score = Math.max(0, Math.min(100,
        Math.min(Math.abs(ltpChangePercent) / 40, 1) * 24 +
        Math.min(Math.abs(deltaChange) / 0.16, 1) * 18 +
        Math.min(Math.max(gammaRatio - 1, 0) / 0.5, 1) * 18 +
        Math.min(Math.max(ivChange, 0) / 3, 1) * 14 +
        Math.min(Math.max(volumeChange, 0) / Math.max(requiredVolume, 1), 1) * 16 +
        (directionConfirmed ? 10 : 0) -
        (thetaDominant ? 15 : 0)
      ));

      const rapidMoment = agreementCount >= 4 && directionConfirmed && !thetaDominant;
      const decayScore = Math.max(0, Math.min(100,
        Math.min(Math.max(-ltpChangePercent, 0) / 35, 1) * 35 +
        Math.min(thetaDrag / Math.max(openLtp * 0.03, 0.1), 1) * 25 +
        Math.min(Math.max(-ivChange, 0) / 3, 1) * 20 +
        Math.min(Math.max(Math.abs(option.openDelta) - Math.abs(option.delta), 0) / 0.12, 1) * 10 +
        Math.min(Math.max(-volumeChange, 0) / Math.max(requiredVolume, 1), 1) * 10 +
        (ltpChange < 0 && !directionConfirmed ? 10 : 0)
      ));

      return {
        ...option,
        spotMove,
        ltpChangePercent,
        gammaRatio,
        requiredVolume,
        directionConfirmed,
        checks,
        agreementCount,
        rapidMoment,
        thetaDominant,
        score: Number(score.toFixed(1)),
        decayScore: Number(decayScore.toFixed(1)),
        modeledMove: Number(modeledMove.toFixed(2)),
        effects: {
          delta: Number(deltaEffect.toFixed(2)),
          gamma: Number(gammaEffect.toFixed(2)),
          vega: Number(vegaEffect.toFixed(2)),
          theta: Number(thetaEffect.toFixed(2)),
        },
      };
    });
}

function indicatorGrade(candidate) {
  if (!candidate) return "NO DATA";
  if (candidate.rapidMoment && candidate.score >= 80) return "INSTITUTIONAL RAPID";
  if (candidate.rapidMoment) return "RAPID MOMENT";
  if (candidate.score >= 65) return "ACCELERATION WATCH";
  if (candidate.score >= 45) return "EARLY BUILDUP";
  return "NO CONFIRMED MOMENT";
}

function volumeSignal(row, intervalMinutes) {
  if (!row?.deltas) return { color: "", field: "", direction: "" };
  const threshold = intervalMinutes === 5 ? 200000 : 30000;
  const ce = number(row.deltas.ceVolume);
  const pe = number(row.deltas.peVolume);
  const bullish = Math.max(
    ce >= threshold ? ce : 0,
    pe <= -threshold ? Math.abs(pe) : 0
  );
  const bearish = Math.max(
    pe >= threshold ? pe : 0,
    ce <= -threshold ? Math.abs(ce) : 0
  );
  if ((!bullish && !bearish) || bullish === bearish) {
    return { color: "", field: "", direction: "" };
  }

  const direction = bullish > bearish ? "bullish" : "bearish";
  const winningStrength = Math.max(bullish, bearish);
  const candidates = [
    { field: "ce", strength: Math.abs(ce) >= threshold ? Math.abs(ce) : 0 },
    { field: "pe", strength: Math.abs(pe) >= threshold ? Math.abs(pe) : 0 },
  ].filter(item => item.strength === winningStrength);

  return {
    color: direction === "bullish" ? "rgba(34,197,94,0.24)" : "rgba(239,68,68,0.24)",
    field: candidates.length === 1 ? candidates[0].field : "",
    direction,
  };
}

function sellerConfirmation(row, intervalMinutes) {
  const signal = volumeSignal(row, intervalMinutes);
  if (signal.direction === "bearish" && number(row.deltas?.ceSellers) > 0) return "ce";
  if (signal.direction === "bullish" && number(row.deltas?.peSellers) > 0) return "pe";
  return "";
}

function greekText(greeks) {
  if (!greeks) return "--";
  return `D ${fmt(greeks.delta, 4)} | G ${fmt(greeks.gamma, 6)} | T ${fmt(greeks.theta, 2)} | V ${fmt(greeks.vega, 2)} | IV ${fmt(greeks.iv, 2)}`;
}

function csvCell(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

export default function DhanStrikeMarketReactionGrid({
  secretClientId = "",
  secretAccessToken = "",
  onOpenMarket,
}) {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [indexFilter, setIndexFilter] = useState("NIFTY");
  const [fromDate, setFromDate] = useState(addDays(localDate(), -13));
  const [toDate, setToDate] = useState(localDate());
  const [sessionDate, setSessionDate] = useState(localDate());
  const [activePeriod, setActivePeriod] = useState(HOURLY_PERIODS[0]);
  const [studyMode, setStudyMode] = useState("1");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [collectorStatus, setCollectorStatus] = useState(null);
  const [liveUpdates, setLiveUpdates] = useState(
    () => localStorage.getItem("dhan_strike_market_live_updates_v1") === "true"
  );
  const [expandedLabel, setExpandedLabel] = useState("");
  const [scrollRequest, setScrollRequest] = useState(0);
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [credentials, setCredentials] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("dhan_strike_market_credentials_v1") || "{}");
      return {
        clientId: saved.clientId || secretClientId,
        accessToken: saved.accessToken || secretAccessToken,
      };
    } catch {
      return { clientId: secretClientId, accessToken: secretAccessToken };
    }
  });
  const [credentialStatus, setCredentialStatus] = useState("");
  const latestRowRef = useRef(null);
  const lastAutoScrolledKey = useRef("");
  const initialLiveUpdates = useRef(liveUpdates);
  const studyInterval = studyMode === "5" ? 5 : 1;

  useEffect(() => {
    setCredentials(current => ({
      clientId: current.clientId || secretClientId,
      accessToken: current.accessToken || secretAccessToken,
    }));
  }, [secretAccessToken, secretClientId]);

  useEffect(() => {
    const loadAdminCredentials = async () => {
      const token = localStorage.getItem("ct_login_token");
      if (!token) return;
      try {
        const response = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        const data = await response.json();
        if (!response.ok || !data.success) return;
        setCredentials(current => ({
          clientId: data.settings?.clientId || current.clientId,
          accessToken: data.settings?.accessToken || current.accessToken,
        }));
      } catch {
        // Props and locally saved credentials remain available as fallbacks.
      }
    };
    loadAdminCredentials();
  }, []);

  const saveCredentials = async () => {
    const next = {
      clientId: credentials.clientId.trim(),
      accessToken: credentials.accessToken.trim(),
    };
    if (!next.clientId || !next.accessToken) {
      setCredentialStatus("Client ID and Access Token are required");
      return;
    }
    setCredentialStatus("Saving...");
    try {
      localStorage.setItem("dhan_strike_market_credentials_v1", JSON.stringify(next));
      const token = localStorage.getItem("ct_login_token");
      const settingsResponse = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(next),
      });
      const settingsData = await settingsResponse.json().catch(() => ({}));
      if (!settingsResponse.ok) throw new Error(settingsData.error || "Credential save failed");

      if (liveUpdates) {
        const collectResponse = await fetch(`${API_BASE}/api/dhan-strike-market-logger/collect`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(next),
        });
        const collectData = await collectResponse.json().catch(() => ({}));
        if (!collectResponse.ok) throw new Error(collectData.error || "Dhan collection failed");
        setCredentialStatus("Saved and Dhan collection requested");
        fetchHistory(true);
      } else {
        setCredentialStatus("Saved. Live updates remain stopped");
      }
    } catch (error) {
      setCredentialStatus(error.message);
    }
  };

  const fetchHistory = useCallback(async (focusLatest = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from: fromDate, to: toDate, index: indexFilter });
      const response = await fetch(`${API_BASE}/api/dhan-strike-market-logger/view?${params}`, { cache: "no-store" });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Strike history fetch failed");
      const records = data.records || [];
      setHistory(records);
      const latestForIndex = records.find(record => record.index === indexFilter);
      if (focusLatest && latestForIndex) {
        setSessionDate(latestForIndex.date);
        if (HOURLY_PERIODS.includes(latestForIndex.period)) {
          setActivePeriod(latestForIndex.period);
        }
      }
      setStatus(`${data.records?.length || 0} strike snapshots loaded`);
    } catch (error) {
      setStatus(error.message);
    } finally {
      setLoading(false);
    }
  }, [fromDate, indexFilter, toDate]);

  useEffect(() => {
    if (!liveUpdates) return;
    fetchHistory(true);
  }, [fetchHistory, liveUpdates]);

  useEffect(() => {
    if (!liveUpdates) {
      setCollectorStatus(null);
      setLoading(false);
      setStatus("Live updates stopped");
      return undefined;
    }

    const loadStatus = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/dhan-strike-market-logger/status`, { cache: "no-store" });
        const data = await response.json();
        setCollectorStatus(response.ok ? data : null);
      } catch {
        setCollectorStatus(null);
      }
    };
    loadStatus();
    const interval = setInterval(() => {
      fetchHistory();
      loadStatus();
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchHistory, liveUpdates]);

  const setBackendCollectors = async enabled => {
    const endpoints = [
      "/api/dhan-market-logger/control",
      "/api/dhan-strike-market-logger/control",
    ];

    await Promise.all(endpoints.map(endpoint =>
      fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      }).then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false) {
          throw new Error(data.error || "Collector control failed");
        }
      })
    ));
  };

  const toggleLiveUpdates = async () => {
    const next = !liveUpdates;
    try {
      await setBackendCollectors(next);
      setLiveUpdates(next);
      localStorage.setItem("dhan_strike_market_live_updates_v1", String(next));
      setStatus(next ? "Live updates started" : "Live updates stopped");
      if (next) fetchHistory(true);
    } catch (error) {
      setStatus(error.message || "Collector control failed");
    }
  };

  useEffect(() => {
    setBackendCollectors(initialLiveUpdates.current).catch(() => {
      setStatus("Collector control unavailable");
    });
  }, []);

  const ledger = useMemo(
    () => buildIntervalLedger(history, sessionDate, indexFilter, activePeriod, studyInterval),
    [activePeriod, history, indexFilter, sessionDate, studyInterval]
  );
  const availableDates = useMemo(
    () => [...new Set(history.filter(row => row.index === indexFilter).map(row => row.date))].sort().reverse(),
    [history, indexFilter]
  );
  const latestPopulatedRow = [...ledger].reverse().find(row => row.open);
  const latestCompletedRow = [...ledger].reverse().find(row => row.close);
  const latestDisplayRow = latestCompletedRow || latestPopulatedRow;
  const indicatorRows = useMemo(
    () => rapidIndicatorRows(latestCompletedRow, 1),
    [latestCompletedRow]
  );
  const rankedRapid = useMemo(
    () => [...indicatorRows].sort((a, b) => b.score - a.score),
    [indicatorRows]
  );
  const topRapid = rankedRapid[0] || null;
  const topCallRapid = rankedRapid.find(row => row.side === "CE") || null;
  const topPutRapid = rankedRapid.find(row => row.side === "PE") || null;
  const topDecay = useMemo(
    () => [...indicatorRows].sort((a, b) => b.decayScore - a.decayScore)[0] || null,
    [indicatorRows]
  );
  const latestCloseAge = latestCompletedRow?.close?.capturedAt
    ? Date.now() - new Date(latestCompletedRow.close.capturedAt).getTime()
    : Infinity;
  const blinkingRowLabel = sessionDate === localDate()
    && latestCompletedRow?.close?.period === activePeriod
    && latestCloseAge <= 180000
    ? latestCompletedRow.label
    : "";

  useEffect(() => {
    if (!latestDisplayRow) return;

    setExpandedLabel(latestDisplayRow.label);
    const scrollKey = `${indexFilter}|${sessionDate}|${activePeriod}|${studyInterval}|${latestDisplayRow.label}|${scrollRequest}`;
    if (lastAutoScrolledKey.current === scrollKey) return;
    lastAutoScrolledKey.current = scrollKey;

    const frame = requestAnimationFrame(() => {
      latestRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => cancelAnimationFrame(frame);
  }, [activePeriod, indexFilter, latestDisplayRow, scrollRequest, sessionDate, studyInterval]);

  const exportCsv = () => {
    const headings = [
      "#", "Interval", "Row Type", "Moneyness", "Side", "Strike", "LTP", `${indexFilter} Spot`,
      "Call Sellers", "CE Sellers Change", "Put Sellers", "PE Sellers Change",
      "CE Volume", "CE Volume Change", "PE Volume", "PE Volume Change",
      "Delta", "Gamma", "Theta", "Vega", "IV", "Impact On Index", "Impact On Strike",
    ];
    const lines = ledger.flatMap(row => {
      const strikeLines = intervalStrikeRows(row).map(option => {
        const isCall = option.side === "CE";
        return [
          row.serial, row.label, "STRIKE", option.moneyness, option.side, option.strike,
          option.ltp, row.open?.spot ?? "",
          isCall ? option.sellers : "",
          isCall ? option.sellerChange ?? "" : "",
          !isCall ? option.sellers : "",
          !isCall ? option.sellerChange ?? "" : "",
          isCall ? option.volume : "",
          isCall ? option.volumeChange ?? "" : "",
          !isCall ? option.volume : "",
          !isCall ? option.volumeChange ?? "" : "",
          option.delta, option.gamma, option.theta, option.vega, option.iv,
          row.indexImpact ?? "", option.ltpChange ?? "",
        ];
      });
      if (!row.open) return strikeLines;
      return [
        ...strikeLines,
        [
          row.serial, row.label, "CE TOTAL", "", "CE", "", "", row.open.spot,
          row.open.ceSellers, row.deltas?.ceSellers ?? "", "", "",
          row.open.ceVolume, row.deltas?.ceVolume ?? "", "", "",
          row.open.callGreeks?.delta ?? "", row.open.callGreeks?.gamma ?? "",
          row.open.callGreeks?.theta ?? "", row.open.callGreeks?.vega ?? "", row.open.callGreeks?.iv ?? "",
          row.indexImpact ?? "",
          row.strikeImpact?.side === "CE"
            ? `${row.strikeImpact.side} ${row.strikeImpact.strike} ${signed(row.strikeImpact.points, 2)}`
            : "",
        ],
        [
          row.serial, row.label, "PE TOTAL", "", "PE", "", "", row.open.spot,
          "", "", row.open.peSellers, row.deltas?.peSellers ?? "",
          "", "", row.open.peVolume, row.deltas?.peVolume ?? "",
          row.open.putGreeks?.delta ?? "", row.open.putGreeks?.gamma ?? "",
          row.open.putGreeks?.theta ?? "", row.open.putGreeks?.vega ?? "", row.open.putGreeks?.iv ?? "",
          row.indexImpact ?? "",
          row.strikeImpact?.side === "PE"
            ? `${row.strikeImpact.side} ${row.strikeImpact.strike} ${signed(row.strikeImpact.points, 2)}`
            : "",
        ],
      ];
    }).map(values => values.map(csvCell).join(","));
    const blob = new Blob([`\uFEFF${headings.map(csvCell).join(",")}\n${lines.join("\n")}`], {
      type: "text/csv;charset=utf-8",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `dhan-strike-market-reaction-${indexFilter}-${sessionDate}-${studyInterval}-minute.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const th = {
    padding: "10px 8px",
    borderBottom: `1px solid ${colors.line}`,
    color: colors.gold,
    textAlign: "right",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    background: "#090702",
    zIndex: 1,
  };
  const td = {
    padding: "9px 8px",
    borderBottom: "1px solid rgba(247,201,72,0.12)",
    textAlign: "right",
    whiteSpace: "nowrap",
    verticalAlign: "top",
  };
  const button = {
    border: `1px solid ${colors.line}`,
    borderRadius: 5,
    background: colors.card,
    color: colors.gold,
    padding: "8px 12px",
    fontWeight: 800,
    cursor: "pointer",
  };

  return (
    <div style={{ minHeight: "100vh", background: colors.background, color: "#e5e7eb", padding: 18, fontFamily: "'JetBrains Mono', monospace" }}>
      <style>{`
        @keyframes strikeFieldBlink {
          0%, 100% { background: transparent; box-shadow: none; }
          50% { background: rgba(247,201,72,0.22); box-shadow: inset 0 0 0 1px rgba(247,201,72,0.82); }
        }
        .strike-field-blink { animation: strikeFieldBlink 0.9s ease-in-out infinite; }
      `}</style>

      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 14, alignItems: "center" }}>
        <div>
          <div style={{ color: colors.dimGold, fontSize: 11, letterSpacing: "0.25em", fontWeight: 800 }}>NEXOVATE MARKET STUDY</div>
          <h1 style={{ color: colors.gold, margin: "6px 0" }}>Dhan Strike Market Reaction</h1>
          <div style={{ color: colors.dimGold, fontSize: 12 }}>{indexFilter} Dhan selected-strike seller, volume, Greeks and premium-impact study</div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button style={button} onClick={() => onOpenMarket ? onOpenMarket() : navigate("/dhan-market-reaction")}><ArrowLeft size={14} style={{ display: "inline", marginRight: 6 }} />Dhan Market Reaction</button>
          <button style={button} onClick={() => setCredentialsOpen(value => !value)}>
            Credentials <ChevronDown size={14} style={{ display: "inline", marginLeft: 6 }} />
          </button>
          <button
            style={{
              ...button,
              color: liveUpdates ? colors.red : colors.green,
              borderColor: liveUpdates ? colors.red : colors.green,
            }}
            onClick={toggleLiveUpdates}
          >
            {liveUpdates
              ? <Square size={14} style={{ display: "inline", marginRight: 6 }} />
              : <Play size={14} style={{ display: "inline", marginRight: 6 }} />}
            {liveUpdates ? "Stop Live" : "Start Live"}
          </button>
          <button
            style={button}
            onClick={() => {
              setScrollRequest(value => value + 1);
              fetchHistory(true);
            }}
          >
            <RefreshCw size={14} style={{ display: "inline", marginRight: 6 }} />Refresh
          </button>
          <button style={{ ...button, color: colors.green }} onClick={exportCsv} disabled={!history.length}><Download size={14} style={{ display: "inline", marginRight: 6 }} />Export Excel CSV</button>
        </div>
      </div>

      {credentialsOpen && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, padding: 12, background: colors.panel, border: `1px solid ${colors.line}`, borderRadius: 8 }}>
          <input
            value={credentials.clientId}
            onChange={event => setCredentials(current => ({ ...current, clientId: event.target.value }))}
            placeholder="Dhan Client ID"
            autoComplete="off"
            style={{ ...button, minWidth: 210 }}
          />
          <input
            type="password"
            value={credentials.accessToken}
            onChange={event => setCredentials(current => ({ ...current, accessToken: event.target.value }))}
            placeholder="Dhan Access Token"
            autoComplete="new-password"
            style={{ ...button, minWidth: 360 }}
          />
          <button style={{ ...button, color: colors.green }} onClick={saveCredentials}>
            <Save size={14} style={{ display: "inline", marginRight: 6 }} />Save
          </button>
          <span style={{ alignSelf: "center", color: credentialStatus.toLowerCase().includes("failed") || credentialStatus.toLowerCase().includes("required") ? colors.red : colors.dimGold, fontSize: 11 }}>
            {credentialStatus || "Auto-filled from AdminDashboard Secret Page Credentials"}
          </span>
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 18, padding: 12, background: colors.panel, border: `1px solid ${colors.line}`, borderRadius: 8 }}>
        <select value={indexFilter} onChange={event => setIndexFilter(event.target.value)} style={button}>
          {INDEXES.map(index => <option key={index}>{index}</option>)}
        </select>
        <input type="date" value={fromDate} onChange={event => setFromDate(event.target.value)} style={button} />
        <input type="date" value={toDate} onChange={event => setToDate(event.target.value)} style={button} />
        <button style={button} onClick={() => { setFromDate(addDays(localDate(), -13)); setToDate(localDate()); }}>Last 14 Days</button>
        <span style={{ marginLeft: "auto", alignSelf: "center", color: colors.dimGold, fontSize: 12 }}>
          {loading ? "Loading..." : status}
        </span>
      </div>

      <div style={{ marginTop: 14, background: colors.panel, border: `1px solid ${colors.line}`, borderRadius: 8, padding: 12 }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <strong style={{ color: colors.gold }}>{indexFilter} STRIKE SESSION SHEETS</strong>
          <select value={sessionDate} onChange={event => setSessionDate(event.target.value)} style={{ ...button, marginLeft: "auto" }}>
            {!availableDates.includes(sessionDate) && <option value={sessionDate}>{sessionDate}</option>}
            {availableDates.map(date => <option key={date} value={date}>{date}</option>)}
          </select>
        </div>

        <div style={{ display: "flex", gap: 7, overflowX: "auto", padding: "12px 0" }}>
          {HOURLY_PERIODS.map((period, position) => (
            <button
              key={period}
              onClick={() => setActivePeriod(period)}
              style={{
                ...button,
                flex: "0 0 auto",
                background: activePeriod === period ? "#006b49" : colors.card,
                color: activePeriod === period ? "#fff" : colors.gold,
                borderColor: activePeriod === period ? colors.green : colors.line,
              }}
            >
              Sheet {position + 1}<br /><span style={{ fontSize: 10 }}>{period}</span>
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 7, marginBottom: 12, flexWrap: "wrap" }}>
          {[
            { id: "1", label: "1 MINUTE" },
            { id: "5", label: "5 MINUTE" },
            { id: "rapid", label: "RAPID INDICATOR" },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setStudyMode(tab.id)}
              style={{
                ...button,
                background: studyMode === tab.id ? "#006b49" : colors.card,
                color: studyMode === tab.id ? "#fff" : colors.gold,
              }}
            >
              {tab.label}
            </button>
          ))}
          <span style={{ marginLeft: "auto", color: collectorStatus?.lastCollectionError ? colors.red : colors.dimGold, fontSize: 10, alignSelf: "center" }}>
            Collector: {!liveUpdates
              ? "page polling stopped"
              : collectorStatus
                ? `Dhan every ${collectorStatus.intervalMinutes} minute`
                : "not reachable"}
            {collectorStatus?.lastCollectionError ? ` | ${collectorStatus.lastCollectionError}` : ""}
          </span>
        </div>

        {studyMode === "rapid" ? (
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ padding: 12, border: `1px solid ${colors.line}`, background: "#050504", color: colors.dimGold, fontSize: 11 }}>
              Latest completed one-minute interval: <strong style={{ color: colors.gold }}>{latestCompletedRow?.label || "--"}</strong>
              {" "} | OTM universe: selected 10 CE OTM + 10 PE OTM strikes.
              {" "}Scores are multi-factor diagnostics, not guaranteed trade outcomes.
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 10 }}>
              {[
                { title: "TOP RAPID CANDIDATE", candidate: topRapid, color: topRapid?.side === "CE" ? colors.green : colors.red },
                { title: "BEST CE OTM", candidate: topCallRapid, color: colors.green },
                { title: "BEST PE OTM", candidate: topPutRapid, color: colors.red },
                { title: "MAX RAPID DECAY", candidate: topDecay, color: "#a78bfa", decay: true },
              ].map(card => (
                <div key={card.title} style={{ border: `1px solid ${colors.line}`, borderRadius: 7, background: colors.card, padding: 13 }}>
                  <div style={{ color: colors.dimGold, fontSize: 10, fontWeight: 900 }}>{card.title}</div>
                  <div style={{ color: card.color, fontSize: 19, fontWeight: 900, marginTop: 5 }}>
                    {card.candidate ? `${card.candidate.moneyness} ${fmt(card.candidate.strike)} ${card.candidate.side}` : "--"}
                  </div>
                  <div style={{ color: colors.gold, fontSize: 11, marginTop: 5 }}>
                    {card.candidate
                      ? card.decay
                        ? `Decay score ${fmt(card.candidate.decayScore, 1)}`
                        : `${indicatorGrade(card.candidate)} | Score ${fmt(card.candidate.score, 1)}`
                      : "Awaiting completed interval"}
                  </div>
                  {card.candidate && (
                    <div style={{ color: colors.dimGold, fontSize: 9, lineHeight: 1.55, marginTop: 6 }}>
                      LTP {fmt(card.candidate.ltp, 2)} ({signed(card.candidate.ltpChangePercent, 1)}%)
                      <br />D {fmt(card.candidate.delta, 4)} ({signed(card.candidate.deltaChange, 4)})
                      <br />G {fmt(card.candidate.gamma, 6)} ({fmt(card.candidate.gammaRatio, 2)}x)
                      <br />IV {fmt(card.candidate.iv, 2)} ({signed(card.candidate.ivChange, 2)})
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ minWidth: 1700, width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                <thead>
                  <tr>
                    {[
                      "RANK", "OTM STRIKE", "GRADE", "SCORE", "LTP +/-", "LTP %", "SPOT MOVE",
                      "DELTA +/-", "GAMMA RATIO", "IV +/-", "VOLUME +/-", "AGREEMENT",
                      "MODEL MOVE", "DELTA EFFECT", "GAMMA EFFECT", "VEGA EFFECT", "THETA EFFECT",
                      "DECAY SCORE",
                    ].map(label => <th key={label} style={{ ...th, position: "static", fontSize: 9 }}>{label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rankedRapid.map((candidate, position) => {
                    const ivSpike = candidate.ivChange >= 2;
                    const background = candidate.rapidMoment
                      ? candidate.side === "CE" ? "rgba(34,197,94,0.22)" : "rgba(239,68,68,0.22)"
                      : candidate.checks.delta && candidate.checks.gamma
                        ? "rgba(247,201,72,0.14)"
                        : ivSpike ? "rgba(168,85,247,0.16)" : "transparent";
                    return (
                      <tr key={`${candidate.side}-${candidate.strike}`} style={{ background }}>
                        <td style={td}>{position + 1}</td>
                        <td style={{ ...td, textAlign: "left", color: candidate.side === "CE" ? colors.green : colors.red, fontWeight: 900 }}>
                          {candidate.moneyness} {fmt(candidate.strike)} {candidate.side}
                        </td>
                        <td style={{ ...td, color: candidate.rapidMoment ? colors.gold : colors.dimGold }}>{indicatorGrade(candidate)}</td>
                        <td style={td}>{fmt(candidate.score, 1)}</td>
                        <td style={{ ...td, color: candidate.ltpChange >= 0 ? colors.green : colors.red }}>{signed(candidate.ltpChange, 2)}</td>
                        <td style={{ ...td, color: candidate.ltpChangePercent >= 0 ? colors.green : colors.red }}>{signed(candidate.ltpChangePercent, 1)}%</td>
                        <td style={td}>{signed(candidate.spotMove, 2)}</td>
                        <td style={td}>{signed(candidate.deltaChange, 4)}</td>
                        <td style={{ ...td, color: candidate.checks.gamma ? colors.gold : "#e5e7eb" }}>{fmt(candidate.gammaRatio, 2)}x</td>
                        <td style={{ ...td, color: ivSpike ? "#c084fc" : "#e5e7eb" }}>{signed(candidate.ivChange, 2)}</td>
                        <td style={td}>{signed(candidate.volumeChange)}</td>
                        <td style={td}>{candidate.agreementCount}/5</td>
                        <td style={td}>{signed(candidate.modeledMove, 2)}</td>
                        <td style={td}>{signed(candidate.effects.delta, 2)}</td>
                        <td style={td}>{signed(candidate.effects.gamma, 2)}</td>
                        <td style={td}>{signed(candidate.effects.vega, 2)}</td>
                        <td style={{ ...td, color: candidate.thetaDominant ? colors.red : colors.dimGold }}>{signed(candidate.effects.theta, 2)}</td>
                        <td style={{ ...td, color: candidate.decayScore >= 65 ? "#c084fc" : colors.dimGold }}>{fmt(candidate.decayScore, 1)}</td>
                      </tr>
                    );
                  })}
                  {!rankedRapid.length && (
                    <tr><td colSpan={18} style={{ ...td, textAlign: "center", color: colors.dimGold }}>Two snapshots are required to calculate the indicator.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10 }}>
              <div style={{ border: `1px solid ${colors.line}`, borderRadius: 7, background: colors.card, padding: 13 }}>
                <div style={{ color: colors.gold, fontSize: 11, fontWeight: 900, marginBottom: 8 }}>INDICATOR LEGEND</div>
                <div style={{ color: colors.green, fontSize: 10, lineHeight: 1.8 }}>Green row: confirmed rapid CE</div>
                <div style={{ color: colors.red, fontSize: 10, lineHeight: 1.8 }}>Red row: confirmed rapid PE</div>
                <div style={{ color: colors.gold, fontSize: 10, lineHeight: 1.8 }}>Gold row: Delta and Gamma acceleration</div>
                <div style={{ color: "#c084fc", fontSize: 10, lineHeight: 1.8 }}>Purple IV cell: IV increased by at least 2 points</div>
                <div style={{ color: "#c084fc", fontSize: 10, lineHeight: 1.8 }}>Purple decay score: strong premium-decay risk</div>
              </div>

              <div style={{ border: `1px solid ${colors.line}`, borderRadius: 7, background: colors.card, padding: 13 }}>
                <div style={{ color: colors.gold, fontSize: 11, fontWeight: 900, marginBottom: 8 }}>GREEK GUIDE</div>
                <div style={{ color: "#e5e7eb", fontSize: 10, lineHeight: 1.8 }}><strong style={{ color: colors.blue }}>Delta:</strong> immediate directional price movement.</div>
                <div style={{ color: "#e5e7eb", fontSize: 10, lineHeight: 1.8 }}><strong style={{ color: colors.gold }}>Gamma:</strong> acceleration of Delta; most useful for explosive near-expiry movement.</div>
                <div style={{ color: "#e5e7eb", fontSize: 10, lineHeight: 1.8 }}><strong style={{ color: "#c084fc" }}>IV:</strong> premium expansion from rising uncertainty.</div>
                <div style={{ color: "#e5e7eb", fontSize: 10, lineHeight: 1.8 }}><strong style={{ color: colors.green }}>Vega:</strong> option-price sensitivity to a one-point change in IV.</div>
                <div style={{ color: "#e5e7eb", fontSize: 10, lineHeight: 1.8 }}><strong style={{ color: colors.red }}>Theta:</strong> time decay; normally works against option buyers and does not create rapid upside.</div>
              </div>
            </div>
          </div>
        ) : (
        <>
        <div style={{ overflowX: "auto" }}>
          <table style={{ minWidth: 1750, width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr>
                {[
                  "#",
                  `${studyInterval === 1 ? "ONE" : "FIVE"} MINUTE`,
                  `${indexFilter} SPOT`,
                  "CALL SELLERS",
                  "CE SELLERS +/-",
                  "PUT SELLERS",
                  "PE SELLERS +/-",
                  "CE VOLUME",
                  "CE VOL +/-",
                  "PE VOLUME",
                  "PE VOL +/-",
                  "CALL GREEKS",
                  "PUT GREEKS",
                  "IMPACT ON INDEX",
                  "IMPACT ON STRIKE",
                ].map((label, index) => (
                  <th key={label} style={{ ...th, textAlign: index < 3 || index > 10 ? "left" : "right" }}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ledger.map(row => {
                const signal = volumeSignal(row, studyInterval);
                const confirmation = sellerConfirmation(row, studyInterval);
                const detailRows = intervalStrikeRows(row);
                const isExpanded = expandedLabel === row.label;
                const isLatest = latestDisplayRow?.label === row.label;
                const isLiveIncomplete = row.open && !row.close;
                return (
                  <React.Fragment key={row.label}>
                    <tr
                      ref={isLatest ? latestRowRef : null}
                      onClick={() => row.open && setExpandedLabel(current => current === row.label ? "" : row.label)}
                      style={{
                        background: signal.color || (row.open ? "transparent" : "rgba(255,255,255,0.015)"),
                        cursor: row.open ? "pointer" : "default",
                      }}
                    >
                      <td style={{ ...td, textAlign: "left", color: colors.dimGold }}>{row.serial}</td>
                      <td style={{ ...td, textAlign: "left", color: colors.gold, fontWeight: 900 }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          {row.open && (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
                          {row.label}
                          {isLiveIncomplete && (
                            <span style={{ color: colors.dimGold, fontSize: 8 }}>LIVE - WAITING FOR CLOSE</span>
                          )}
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: "left", color: colors.blue, fontWeight: 900 }}>
                        {row.open ? fmt(row.open.spot, 2) : "--"}
                      </td>
                      <td style={{ ...td, color: colors.red }}>{row.open ? fmt(row.open.ceSellers) : "--"}</td>
                      <td
                        className={
                          confirmation === "ce" ||
                          (row.label === blinkingRowLabel && number(row.deltas?.ceSellers) !== 0)
                            ? "strike-field-blink"
                            : ""
                        }
                        style={{ ...td, color: number(row.deltas?.ceSellers) <= 0 ? colors.green : colors.red }}
                      >
                        {row.deltas ? signed(row.deltas.ceSellers) : "--"}
                      </td>
                      <td style={{ ...td, color: colors.green }}>{row.open ? fmt(row.open.peSellers) : "--"}</td>
                      <td
                        className={
                          confirmation === "pe" ||
                          (row.label === blinkingRowLabel && number(row.deltas?.peSellers) !== 0)
                            ? "strike-field-blink"
                            : ""
                        }
                        style={{ ...td, color: number(row.deltas?.peSellers) >= 0 ? colors.green : colors.red }}
                      >
                        {row.deltas ? signed(row.deltas.peSellers) : "--"}
                      </td>
                      <td style={td}>{row.open ? fmt(row.open.ceVolume) : "--"}</td>
                      <td className={signal.field === "ce" ? "strike-field-blink" : ""} style={td}>
                        {row.deltas ? signed(row.deltas.ceVolume) : "--"}
                      </td>
                      <td style={td}>{row.open ? fmt(row.open.peVolume) : "--"}</td>
                      <td className={signal.field === "pe" ? "strike-field-blink" : ""} style={td}>
                        {row.deltas ? signed(row.deltas.peVolume) : "--"}
                      </td>
                      <td style={{ ...td, textAlign: "left", minWidth: 190 }}>
                        {row.open ? greekText(row.open.callGreeks) : "--"}
                      </td>
                      <td style={{ ...td, textAlign: "left", minWidth: 190 }}>
                        {row.open ? greekText(row.open.putGreeks) : "--"}
                      </td>
                      <td style={{ ...td, textAlign: "left", color: number(row.indexImpact) >= 0 ? colors.green : colors.red }}>
                        {row.close ? `${signed(row.indexImpact, 2)} pts` : "--"}
                      </td>
                      <td style={{ ...td, textAlign: "left", color: number(row.strikeImpact?.points) >= 0 ? colors.green : colors.red }}>
                        {row.strikeImpact
                          ? `${row.strikeImpact.side} ${fmt(row.strikeImpact.strike)} ${signed(row.strikeImpact.points, 2)} pts`
                          : "--"}
                      </td>
                    </tr>

                    {isExpanded && row.open && (
                      <tr>
                        <td colSpan={15} style={{ padding: 0, borderBottom: `1px solid ${colors.line}` }}>
                          <div style={{ padding: 10, background: "#050504", overflowX: "auto" }}>
                            <table style={{ minWidth: 1700, width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                              <thead>
                                <tr>
                                  {[
                                    "TYPE / STRIKE",
                                    "LTP",
                                    `${indexFilter} SPOT`,
                                    "CALL SELLERS",
                                    "CE SELLERS +/-",
                                    "PUT SELLERS",
                                    "PE SELLERS +/-",
                                    "CE VOLUME",
                                    "CE VOL +/-",
                                    "PE VOLUME",
                                    "PE VOL +/-",
                                    "DELTA",
                                    "GAMMA",
                                    "THETA",
                                    "VEGA",
                                    "IV",
                                    "IMPACT ON INDEX",
                                    "IMPACT ON STRIKE",
                                  ].map(label => (
                                    <th key={label} style={{ ...th, position: "static", fontSize: 9 }}>{label}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {detailRows.map(option => {
                                  const isCall = option.side === "CE";
                                  return (
                                    <tr key={`${option.side}-${option.strike}`}>
                                      <td style={{ ...td, textAlign: "left", color: option.moneyness === "ATM" ? colors.gold : option.moneyness === "ITM" ? colors.green : colors.blue, fontWeight: 900 }}>
                                        {option.moneyness} - {fmt(option.strike)} {option.side}
                                      </td>
                                      <td style={td}>{fmt(option.ltp, 2)}</td>
                                      <td style={{ ...td, color: colors.blue }}>{fmt(row.open.spot, 2)}</td>
                                      <td style={{ ...td, color: colors.red }}>{isCall ? fmt(option.sellers) : "--"}</td>
                                      <td style={{ ...td, color: number(option.sellerChange) <= 0 ? colors.green : colors.red }}>
                                        {isCall && option.sellerChange !== null ? signed(option.sellerChange) : "--"}
                                      </td>
                                      <td style={{ ...td, color: colors.green }}>{!isCall ? fmt(option.sellers) : "--"}</td>
                                      <td style={{ ...td, color: number(option.sellerChange) >= 0 ? colors.green : colors.red }}>
                                        {!isCall && option.sellerChange !== null ? signed(option.sellerChange) : "--"}
                                      </td>
                                      <td style={td}>{isCall ? fmt(option.volume) : "--"}</td>
                                      <td style={td}>{isCall && option.volumeChange !== null ? signed(option.volumeChange) : "--"}</td>
                                      <td style={td}>{!isCall ? fmt(option.volume) : "--"}</td>
                                      <td style={td}>{!isCall && option.volumeChange !== null ? signed(option.volumeChange) : "--"}</td>
                                      <td style={td}>{fmt(option.delta, 4)}</td>
                                      <td style={td}>{fmt(option.gamma, 6)}</td>
                                      <td style={td}>{fmt(option.theta, 2)}</td>
                                      <td style={td}>{fmt(option.vega, 2)}</td>
                                      <td style={td}>{fmt(option.iv, 2)}</td>
                                      <td style={{ ...td, color: number(row.indexImpact) >= 0 ? colors.green : colors.red }}>
                                        {row.close ? `${signed(row.indexImpact, 2)} pts` : "--"}
                                      </td>
                                      <td style={{ ...td, color: number(option.ltpChange) >= 0 ? colors.green : colors.red }}>
                                        {option.ltpChange !== null ? `${signed(option.ltpChange, 2)} pts` : "--"}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr style={{ background: "rgba(239,68,68,0.13)", color: colors.gold, fontWeight: 900 }}>
                                  <td style={{ ...td, textAlign: "left" }}>CE TOTAL (1 ITM + ATM + 10 OTM)</td>
                                  <td style={td}>--</td>
                                  <td style={{ ...td, color: colors.blue }}>{fmt(row.open.spot, 2)}</td>
                                  <td style={td}>{fmt(row.open.ceSellers)}</td>
                                  <td style={td}>{row.deltas ? signed(row.deltas.ceSellers) : "--"}</td>
                                  <td style={td}>--</td>
                                  <td style={td}>--</td>
                                  <td style={td}>{fmt(row.open.ceVolume)}</td>
                                  <td style={td}>{row.deltas ? signed(row.deltas.ceVolume) : "--"}</td>
                                  <td style={td}>--</td>
                                  <td style={td}>--</td>
                                  <td style={td}>{fmt(row.open.callGreeks?.delta, 4)}</td>
                                  <td style={td}>{fmt(row.open.callGreeks?.gamma, 6)}</td>
                                  <td style={td}>{fmt(row.open.callGreeks?.theta, 2)}</td>
                                  <td style={td}>{fmt(row.open.callGreeks?.vega, 2)}</td>
                                  <td style={td}>{fmt(row.open.callGreeks?.iv, 2)}</td>
                                  <td style={td}>{row.close ? `${signed(row.indexImpact, 2)} pts` : "--"}</td>
                                  <td style={td}>
                                    {row.strikeImpact?.side === "CE"
                                      ? `${row.strikeImpact.side} ${fmt(row.strikeImpact.strike)} ${signed(row.strikeImpact.points, 2)} pts`
                                      : "--"}
                                  </td>
                                </tr>
                                <tr style={{ background: "rgba(34,197,94,0.13)", color: colors.gold, fontWeight: 900 }}>
                                  <td style={{ ...td, textAlign: "left" }}>PE TOTAL (1 ITM + ATM + 10 OTM)</td>
                                  <td style={td}>--</td>
                                  <td style={{ ...td, color: colors.blue }}>{fmt(row.open.spot, 2)}</td>
                                  <td style={td}>--</td>
                                  <td style={td}>--</td>
                                  <td style={td}>{fmt(row.open.peSellers)}</td>
                                  <td style={td}>{row.deltas ? signed(row.deltas.peSellers) : "--"}</td>
                                  <td style={td}>--</td>
                                  <td style={td}>--</td>
                                  <td style={td}>{fmt(row.open.peVolume)}</td>
                                  <td style={td}>{row.deltas ? signed(row.deltas.peVolume) : "--"}</td>
                                  <td style={td}>{fmt(row.open.putGreeks?.delta, 4)}</td>
                                  <td style={td}>{fmt(row.open.putGreeks?.gamma, 6)}</td>
                                  <td style={td}>{fmt(row.open.putGreeks?.theta, 2)}</td>
                                  <td style={td}>{fmt(row.open.putGreeks?.vega, 2)}</td>
                                  <td style={td}>{fmt(row.open.putGreeks?.iv, 2)}</td>
                                  <td style={td}>{row.close ? `${signed(row.indexImpact, 2)} pts` : "--"}</td>
                                  <td style={td}>
                                    {row.strikeImpact?.side === "PE"
                                      ? `${row.strikeImpact.side} ${fmt(row.strikeImpact.strike)} ${signed(row.strikeImpact.points, 2)} pts`
                                      : "--"}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {studyMode === "5" && (
          <div style={{ marginTop: 12, border: `1px solid ${colors.line}`, borderRadius: 7, background: colors.card, padding: 13, color: colors.dimGold, fontSize: 10, lineHeight: 1.8 }}>
            <div>1-min threshold: 30,000 volume change.</div>
            <div>5-min threshold: 2,00,000 volume change.</div>
            <div style={{ color: colors.green }}>Green row: CE volume rises or PE volume falls.</div>
            <div style={{ color: colors.red }}>Red row: PE volume rises or CE volume falls.</div>
            <div>Larger signal wins when both qualify.</div>
            <div>The responsible CE/PE volume field blinks.</div>
            <div>Confirming seller-change field also blinks.</div>
            <div>Latest seller changes blink for up to three minutes.</div>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
